import React, { useCallback } from 'react'
import { FormikProps, useFormikContext } from 'formik'
import { OfferStakeholders } from '../../../types/flyout'
import { MultiSelectV4, TextAreaWithChipsV4 } from '@labrav/react-components'
import { useStyles } from './EditOfferStakeholdersForm.styles'
import {
  DataType,
  FormField,
  SelectorOptions,
} from '@labrav/react-components/lib/@types/types/forms'
import { get } from 'lodash'
import { MatchString } from '@labrav/common'
import clsx from 'clsx'
import { StakeholderUserOption } from '../ViewStakeholders/stakeholderUserOption'

export type EditOfferStakeholdersFormProps = {
  hideStakeholders?: boolean
  hideHeading?: boolean
  users?: StakeholderUserOption[]
}

export const EditOfferStakeholdersForm: React.FC<
  EditOfferStakeholdersFormProps
> = ({ hideStakeholders = false, hideHeading = false, users = [] }) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext() as FormikProps<Record<string, unknown>>
  const { classes } = useStyles()
  const offerStakeholders =
    (values?.offerStakeholders as OfferStakeholders[]) || []
  const customOfferStakeholders =
    (values?.customOfferStakeholders as OfferStakeholders[]) || []

  const allUsers = users
  const emailsAndNames = allUsers.map(user => ({
    email: user.email,
    name: user?.name.trim() ? user.name : user.email,
  }))
  const options: SelectorOptions[] = emailsAndNames.map(({ email, name }) => ({
    value: email,
    label: name,
  }))

  const multiSelectData: FormField = {
    title: 'Select stakeholders',
    placeHolder: 'Select stakeholders',
    humanReadableKey: 'offerStakeholders',
    id: 'offerStakeholders',
    isReadOnly: false,
    isRequired: true,
    defaultValue: undefined,
    dataType: 'multi_enum' as DataType,
    validationType: undefined,
    isCustom: false,
    options: options,
  }
  const handleInputChangeSelect = useCallback(
    (event: unknown) => {
      const selectedValues = (event as React.ChangeEvent<HTMLInputElement>)
        .target.value as unknown as string[]

      const selectedUsers = selectedValues?.map((value: string) => {
        const selectedUser = allUsers.find(user => user.email === value)
        return {
          email: selectedUser?.email,
          firstName: selectedUser?.firstName,
          lastName: selectedUser?.lastName,
        }
      })
      setFieldValue('offerStakeholders', [...selectedUsers])
    },
    [allUsers, setFieldValue]
  )

  const handleTextAreaChange = useCallback(
    (value: string) => {
      const email = value
      if (!email) return
      const existingEmails = offerStakeholders.map(
        stakeholder => stakeholder.email
      )
      const customEmails = customOfferStakeholders.map(
        customEmail => customEmail.email
      )

      if (
        MatchString(existingEmails, value) &&
        MatchString(customEmails, value)
      ) {
        const newStakeholder = { email: email }
        const updatedCustomStakeholders = [
          ...customOfferStakeholders,
          newStakeholder,
        ]
        setFieldValue('customOfferStakeholders', updatedCustomStakeholders)
      }
    },
    [offerStakeholders, customOfferStakeholders, setFieldValue]
  )

  const handleDeleteChip = (value: string, key: string) => {
    setFieldTouched('offerStakeholders', true)
    if (key === 'offerStakeholders') {
      const filteredOptions = [
        ...offerStakeholders.filter(option => option.email !== value),
      ]
      setFieldValue('offerStakeholders', filteredOptions)
    } else {
      const filteredOptions = [
        ...customOfferStakeholders.filter(option => option.email !== value),
      ]
      setFieldValue('customOfferStakeholders', filteredOptions)
    }
  }
  return (
    <div
      data-testid="Stakeholders-section"
      className={clsx(
        classes.sectionWrapper,
        hideHeading ? classes.dialog : ''
      )}
    >
      {!hideStakeholders && (
        <div className={classes.sectionContainer}>
          <MultiSelectV4
            field={multiSelectData}
            onChangeSelect={handleInputChangeSelect}
            selectedValues={offerStakeholders?.map(user => user.email) || []}
            handleDelete={value => handleDeleteChip(value, 'offerStakeholders')}
            isError={
              !!(
                get(touched, 'offerStakeholders', false) &&
                get(errors, 'offerStakeholders', false)
              )
            }
            errorText={
              (!!get(touched, 'offerStakeholders', false) &&
                get(errors, 'offerStakeholders', '')) ||
              ''
            }
            onBlur={handleBlur}
            showInsideInfoIcon={true}
            showInsideInfoIconTooltip={{
              title: '',
              content:
                'Add team members with access to this Labra account to receive this offer-related event notifications.',
            }}
          />
        </div>
      )}
      <div className={classes.sectionContainer}>
        <div className={classes.widthTextArea}>
          <TextAreaWithChipsV4
            id={'customOfferStakeholders'}
            label="Add stakeholders emails (Optional)"
            selectedValues={
              customOfferStakeholders.map(user => user.email) || []
            }
            onChange={handleTextAreaChange}
            handleDelete={value =>
              handleDeleteChip(value, 'customOfferStakeholders')
            }
            showSpinner={false}
            isError={
              !!(
                get(touched, 'customOfferStakeholders', false) &&
                get(errors, 'customOfferStakeholders', false)
              )
            }
            helperText={
              (!!get(touched, 'customOfferStakeholders', false) &&
                get(errors, 'customOfferStakeholders', '')) ||
              ''
            }
            onBlur={handleBlur}
            supportingText={'Please press enter or put comma to add emails.'}
            showInsideInfoIcon={true}
            showInsideInfoIconTooltip={{
              title: '',
              content:
                'Add the email addresses of individual who do not have access to the Labra account but would like to be notified about this offer related events.',
            }}
          />
        </div>
      </div>
    </div>
  )
}
